import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import Header, { HeaderLeft, HeaderRight } from '../../../layout/Header/Header';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Icon from '../../../components/icon/Icon';
import { otIconFor } from '../../../components/icon/svg-icons/ot/otIconFor';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Popovers from '../../../components/bootstrap/Popovers';
import Spinner from '../../../components/bootstrap/Spinner';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';
import { UserType } from '../../../common/data/dummyRolesData';
import LANG, { getLangWithKey, ILang } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import { useOrganization } from '../../../contexts/organizationContext';
import OrganizationDropdown from '../../../components/Header/OrganizationDropdown';
import NotificationBell from '../../../components/notifications/NotificationBell';
import { displayFirstName } from '../../../helpers/helpers';

const DashboardHeader: React.FC = () => {
	const { t, i18n } = useTranslation(['dashboard', 'settings']);
	const { userData } = useContext(AuthContext);
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const isCompany = userData?.role === UserType.COMPANY;
	/* A greeting must never read "Buon pomeriggio," with nothing after the comma: the identity
	   claims are optional, so the name goes through the shared fallback chain and, when nothing
	   identifies the user, the greeting drops the comma entirely (US-AUTH-01). */
	const firstName = displayFirstName(userData, '');

	const { selectedOrganization } = useOrganization();

	const [currentTime, setCurrentTime] = useState(new Date());

	// Update time every second for the clock
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 1000);
		return () => clearInterval(timer);
	}, []);

	// Button style
	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	// Format time
	const formatTime = (date: Date) => {
		return date.toLocaleTimeString(i18n.language, {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	// Format date
	const formatDate = (date: Date) => {
		return date.toLocaleDateString(i18n.language, {
			weekday: 'short',
			day: 'numeric',
			month: 'short',
		});
	};

	/**
	 * Which part of the day it is. Four bands, not three: at one in the morning "Buonasera" is
	 * wrong, and the mark beside the greeting should be the moon, not the evening sun.
	 */
	const timeBand = (hour: number): 'morning' | 'afternoon' | 'evening' | 'night' => {
		if (hour < 5) return 'night';
		if (hour < 12) return 'morning';
		if (hour < 18) return 'afternoon';
		if (hour < 23) return 'evening';
		return 'night';
	};

	const band = timeBand(currentTime.getHours());

	/**
	 * The greeting, drawn at random from the variants for this band.
	 *
	 * Memoised on the band, not on the clock: the header re-renders every minute, and a line that
	 * reshuffles under the reader is a distraction rather than a welcome. It changes when the part
	 * of the day changes, or when the language does.
	 */
	const greeting = useMemo(() => {
		const variants = t(`welcome.greetings.${band}`, { returnObjects: true });
		const list = Array.isArray(variants) ? (variants as string[]) : [String(variants)];
		return list[Math.floor(Math.random() * list.length)];
	}, [band, t]);

	/**
	 * Switches the application language and shows a confirmation notification.
	 * Same behaviour as in CommonHeaderRight and SimpleHeader: delegates to
	 * `i18n.changeLanguage()`, triggering a re-render of all translated
	 * components, and persists the choice via LanguageDetector (localStorage).
	 *
	 * @userstory US-I18N-02 — Switch language at any time without logging out
	 * @userstory US-I18N-03 — Preference is automatically saved by LanguageDetector
	 */
	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then(() => {
			showNotification(
				<span className='d-flex align-items-center'>
					<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
					<span>
						{t('settings:languageChanged', { language: getLangWithKey(lng)?.text })}
					</span>
				</span>,
				t('settings:languageUpdated'),
			);
		});
	};

	// ============================================
	// MOBILE CENTER CONTENT (shown in mobile-header bar)
	// ============================================
	/**
	 * Time-of-day mark (US-UI-40). The review missed the little sun that used to warm up the
	 * header, and it was right: the page had gone inert. It is back as an illustrated icon — never
	 * an emoji (BR-UI-05), which renders differently on every platform and belongs to no brand —
	 * and with no tinted plate behind it: the sun is the light, not a glyph sitting on a square.
	 */
	const timeOfDay = (): { icon: string; tone: 'warm' | 'brand' } => {
		if (band === 'morning') return { icon: 'WbSunny', tone: 'warm' };
		if (band === 'afternoon') return { icon: 'WbTwilight', tone: 'warm' };
		return { icon: 'NightsStay', tone: 'brand' };
	};

	const greetingMark = (
		<span className={`ot-header__mark ot-header__mark--${timeOfDay().tone}`} aria-hidden='true'>
			<Icon icon={otIconFor(timeOfDay().icon)} />
		</span>
	);

	const mobileGreeting = (
		<span className='ot-header__greeting'>
			{greetingMark}
			<span>
				{greeting}
				{firstName && <strong> {firstName}</strong>}
				{t('welcome.greetingSuffix')}
			</span>
		</span>
	);

	return (
		<Header mobileCenter={mobileGreeting}>
			{/* ============================================ */}
			{/* DESKTOP HEADER LEFT - Greeting */}
			{/* ============================================ */}
			<HeaderLeft>
				<div className='d-flex align-items-center w-100'>
					{/* One status line (US-UI-36, BR-UI-27). The greeting used to carry a
					    time-of-day emoji on an infinite bounce animation, a gradient-filled name,
					    and a motivational subtitle — while the dashboard below said "everything is
					    fine" twice more. The header now states who you are and nothing else. */}
					{/* First name, no comma, one exclamation: this line is a greeting, not a record.
					    "Buon pomeriggio, Jonathan Loise" reads like a letterhead; "Ciao Jonathan!"
					    reads like someone is glad you came in. The time of day is carried by the
					    mark beside it — the sun, or the moon after six.

					    The words live in a single child so the row's `gap` falls only between the
					    mark and the sentence, instead of prising apart every word of it. */}
					<span className='ot-header__greeting'>
						{greetingMark}
						<span>
							{greeting}
							{firstName && <strong> {firstName}</strong>}
							{t('welcome.greetingSuffix')}
						</span>
					</span>

					{/* Organization Switcher for Company users */}
					{isCompany && (
						<div className='ms-auto d-none d-md-flex align-items-center'>
							<div className='bg-l10-primary rounded px-3 py-2'>
								<OrganizationDropdown />
							</div>
						</div>
					)}
				</div>
			</HeaderLeft>

			{/* ============================================ */}
			{/* HEADER RIGHT - Mobile panel + Desktop controls */}
			{/* ============================================ */}
			<HeaderRight>
				{/* ----- MOBILE PANEL CONTENT (< md) ----- */}
				<div className='d-md-none d-flex flex-column align-items-center w-100 py-4 px-3'>
					{/* Organization Switcher for Company users (Mobile) */}
					{isCompany && (
						<div className='mb-4 w-100'>
							<div className='bg-l10-primary rounded px-3 py-2 text-center'>
								<OrganizationDropdown />
							</div>
						</div>
					)}

					{/* Time & Date Section */}
					<div
						className='text-center mb-4 pb-3 w-100 rounded-3 py-3'
						style={{
							background: darkModeStatus
								? 'rgba(255,255,255,0.03)'
								: 'rgba(0,0,0,0.02)',
						}}>
						<motion.div
							initial={{ scale: 0.9 }}
							animate={{ scale: 1 }}
							className='fw-bold mb-1'
							style={{
								fontSize: '2.5rem',
								fontFamily: 'monospace',
								color: 'var(--ot-text-heading)',
								letterSpacing: '3px',
							}}>
							{formatTime(currentTime)}
						</motion.div>
						<div
							className={classNames({
								'text-light opacity-75': darkModeStatus,
								'text-muted': !darkModeStatus,
							})}
							style={{ textTransform: 'capitalize', fontSize: '0.95rem' }}>
							{formatDate(currentTime)}
						</div>
					</div>

					<hr
						className={classNames('w-75 my-3', {
							'border-secondary': darkModeStatus,
						})}
					/>

					{/* Two buttons centered */}
					<div className='d-flex justify-content-center gap-4 mt-2'>
						<Popovers trigger='hover' desc='Dark / Light mode'>
							<Button
								{...styledBtn}
								onClick={() => setDarkModeStatus(!darkModeStatus)}
								className='btn-only-icon'
								aria-label='Toggle dark mode'>
								<Icon
									icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
									color={darkModeStatus ? 'info' : 'warning'}
									className='btn-icon'
								/>
							</Button>
						</Popovers>

						<Dropdown>
							<DropdownToggle hasIcon={false}>
								{typeof getLangWithKey(i18n.language as ILang['key']['lng'])
									?.icon === 'undefined' ? (
									<Button
										{...styledBtn}
										className='btn-only-icon'
										aria-label='Change language'>
										<Spinner isSmall inButton='onlyIcon' isGrow />
									</Button>
								) : (
									<Button
										{...styledBtn}
										icon={
											getLangWithKey(i18n.language as ILang['key']['lng'])
												?.icon
										}
										aria-label='Change language'
									/>
								)}
							</DropdownToggle>
							<DropdownMenu isAlignmentEnd>
								{Object.keys(LANG).map((i) => (
									<DropdownItem key={LANG[i].lng}>
										<Button
											icon={LANG[i].icon}
											onClick={() => changeLanguage(LANG[i].lng)}>
											{LANG[i].text}
										</Button>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
					</div>
				</div>

				{/* ----- DESKTOP CONTROLS (md+) ----- */}
				<div className='d-none d-md-block'>
					<div className='row g-3 align-items-center'>
						{/* Notification Bell */}
						<NotificationBell styledBtn={styledBtn} />

						{/* Dark Mode Toggle */}
						<div className='col-auto'>
							<Popovers trigger='hover' desc='Dark / Light mode'>
								<Button
									{...styledBtn}
									onClick={() => setDarkModeStatus(!darkModeStatus)}
									className='btn-only-icon'
									data-tour='dark-mode'
									aria-label='Toggle dark mode'>
									<Icon
										icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
										color={darkModeStatus ? 'info' : 'warning'}
										className='btn-icon'
									/>
								</Button>
							</Popovers>
						</div>

						{/* Language Selector */}
						<div className='col-auto'>
							<Dropdown>
								<DropdownToggle hasIcon={false}>
									{typeof getLangWithKey(i18n.language as ILang['key']['lng'])
										?.icon === 'undefined' ? (
										<Button
											{...styledBtn}
											className='btn-only-icon'
											aria-label='Change language'
											data-tour='lang-selector'>
											<Spinner isSmall inButton='onlyIcon' isGrow />
										</Button>
									) : (
										<Button
											{...styledBtn}
											icon={
												getLangWithKey(i18n.language as ILang['key']['lng'])
													?.icon
											}
											aria-label='Change language'
											data-tour='lang-selector'
										/>
									)}
								</DropdownToggle>
								<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
									{Object.keys(LANG).map((i) => (
										<DropdownItem key={LANG[i].lng}>
											<Button
												icon={LANG[i].icon}
												onClick={() => changeLanguage(LANG[i].lng)}>
												{LANG[i].text}
											</Button>
										</DropdownItem>
									))}
								</DropdownMenu>
							</Dropdown>
						</div>
					</div>
				</div>
			</HeaderRight>
		</Header>
	);
};

export default DashboardHeader;
